"use client"
import { useParams } from "react-router-dom"
import { Container, Row, Col, Button, Card, Dropdown } from "react-bootstrap"
import "./styled/Solution.css"

function SolutionDetail() {
  const { solutionId } = useParams()

  // Background images for each solution type
  const backgroundImages = {
    residential:
      "https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    commercial:
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    industrial:
      "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    specialized:
      "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    maintenance:
      "https://images.unsplash.com/photo-1563453392212-326f5e854473?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    sanitization:
      "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
  }

  // This would typically come from an API or data source
  const solutionDetails = {
    residential: {
      title: "Residential Cleaning Services",
      description: "Professional cleaning services for homes and apartments",
      content:
        "Our residential cleaning services are designed to keep your home spotless and healthy. We offer regular cleaning, deep cleaning, and specialized services tailored to your specific needs. Our trained professionals use eco-friendly products and advanced techniques to ensure thorough cleaning.",
      features: [
        "Regular maintenance cleaning",
        "Deep cleaning services",
        "Move-in/move-out cleaning",
        "Spring cleaning",
        "Eco-friendly cleaning options",
      ],
      subServices: [
        {
          title: "Regular Maintenance",
          description: "Weekly or bi-weekly cleaning to maintain a clean home environment",
        },
        {
          title: "Deep Cleaning",
          description: "Thorough cleaning of all areas including hard-to-reach places",
        },
        {
          title: "Move-in/Move-out",
          description: "Comprehensive cleaning for property transitions",
        },
      ],
    },
    commercial: {
      title: "Commercial Cleaning Services",
      description: "Professional cleaning solutions for businesses and commercial spaces",
      content:
        "SunShiny provides professional commercial cleaning services for offices, retail spaces, healthcare facilities, and educational institutions. We understand that a clean workplace enhances productivity and creates a positive impression on clients and employees.",
      features: [
        "Office cleaning",
        "Retail space maintenance",
        "Healthcare facility sanitization",
        "Educational institution cleaning",
        "Customized cleaning schedules",
      ],
      subServices: [
        {
          title: "Office Cleaning",
          description: "Regular cleaning services for office spaces",
        },
        {
          title: "Retail Maintenance",
          description: "Specialized cleaning for retail environments",
        },
        {
          title: "Healthcare Facilities",
          description: "Sanitization services for medical offices and facilities",
        },
      ],
    },
    industrial: {
      title: "Industrial Cleaning Solutions",
      description: "Specialized cleaning for industrial facilities and equipment",
      content:
        "Our industrial cleaning solutions address the unique challenges of manufacturing facilities, warehouses, and production plants. We provide specialized cleaning services for industrial equipment, machinery, and facilities, ensuring compliance with safety and hygiene regulations.",
      features: [
        "Manufacturing facility cleaning",
        "Warehouse maintenance",
        "Equipment and machinery cleaning",
        "Safety compliance services",
        "Specialized industrial solutions",
      ],
      subServices: [
        {
          title: "Manufacturing Facilities",
          description: "Comprehensive cleaning for production environments",
        },
        {
          title: "Warehouse Cleaning",
          description: "Specialized services for large warehouse spaces",
        },
        {
          title: "Equipment Maintenance",
          description: "Cleaning and maintenance for industrial machinery",
        },
      ],
    },
    specialized: {
      title: "Specialized Cleaning Services",
      description: "Unique cleaning solutions for specific requirements",
      content:
        "We offer specialized cleaning services for unique requirements, including post-construction cleanup, carpet and upholstery cleaning, and window washing.",
      features: [
        "Post-construction cleanup",
        "Carpet & upholstery cleaning",
        "Window washing",
        "Floor maintenance",
        "High-pressure cleaning",
      ],
      subServices: [
        {
          title: "Post-Construction",
          description: "Thorough cleaning after construction or renovation projects",
        },
        {
          title: "Carpet & Upholstery",
          description: "Deep cleaning for carpets, rugs, and furniture",
        },
        {
          title: "Window Services",
          description: "Professional window cleaning for all building types",
        },
      ],
    },
    maintenance: {
      title: "Maintenance Services",
      description: "Regular upkeep and preventative maintenance",
      content:
        "Our maintenance services help keep your property in top condition with regular upkeep, minor repairs, and preventative maintenance solutions.",
      features: [
        "Regular property maintenance",
        "Minor repairs",
        "Preventative maintenance",
        "Seasonal services",
        "Property inspections",
      ],
      subServices: [
        {
          title: "Regular Upkeep",
          description: "Scheduled maintenance to prevent issues",
        },
        {
          title: "Minor Repairs",
          description: "Quick fixes for common property problems",
        },
        {
          title: "Seasonal Services",
          description: "Specialized maintenance based on seasonal needs",
        },
      ],
    },
    sanitization: {
      title: "Sanitization Services",
      description: "Thorough disinfection for healthy environments",
      content:
        "Our sanitization services provide thorough disinfection of spaces to eliminate germs, bacteria, and viruses, ensuring a healthy environment.",
      features: [
        "Deep disinfection",
        "Germ elimination",
        "Regular sanitization programs",
        "Outbreak prevention",
        "Health-focused cleaning",
      ],
      subServices: [
        {
          title: "Deep Disinfection",
          description: "Thorough sanitization of all surfaces",
        },
        {
          title: "Regular Programs",
          description: "Scheduled sanitization to maintain healthy environments",
        },
        {
          title: "Outbreak Prevention",
          description: "Specialized services to prevent the spread of illness",
        },
      ],
    },
  }

  const solution = solutionDetails[solutionId] || {
    title: "Solution",
    description: "Details coming soon",
    content:
      "We're currently updating information for this solution. Please check back later or contact us for more details.",
    features: [],
    subServices: [],
  }

  // Get all solution types for the dropdown
  const allSolutions = Object.keys(solutionDetails).map((key) => ({
    id: key,
    title: solutionDetails[key].title,
  }))

  return (
    <>
      {/* Hero Section with Background Image */}
      <div
        className="solution-hero"
        style={{
          backgroundImage: `url('${backgroundImages[solutionId] || "https://images.unsplash.com/photo-1584622781564-1d987f7333c1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"}')`,
        }}
      >
        <div className="solution-hero-overlay">
          <Container>
            <Row className="align-items-center">
              <Col lg={8} className="text-white">
                <h1 className="display-4 fw-bold mb-4">{solution.title}</h1>
                <p className="lead mb-4">{solution.description}</p>
                <Button variant="danger" size="lg" className="px-4 py-2">
                  Get a Free Quote
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </div>

      {/* Solution Navigation */}
      <div className="bg-light py-3">
        <Container>
          <Row>
            <Col>
              <Dropdown>
                <Dropdown.Toggle variant="outline-primary" id="dropdown-solutions">
                  Browse All Solutions
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {allSolutions.map((item) => (
                    <Dropdown.Item key={item.id} href={`/solutions/${item.id}`}>
                      {item.title}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        <Row className="g-5">
          <Col lg={8}>
            <div className="mb-5">
              <h2 className="fw-bold mb-4">Overview</h2>
              <p className="lead">{solution.content}</p>
            </div>

            {/* Sub-services Section with Dropdowns */}
            {solution.subServices && solution.subServices.length > 0 && (
              <div className="mb-5">
                <h2 className="fw-bold mb-4">Our {solution.title} Include</h2>
                <div className="accordion" id="servicesAccordion">
                  {solution.subServices.map((subService, index) => (
                    <div className="accordion-item mb-3 border rounded shadow-sm" key={index}>
                      <h3 className="accordion-header" id={`heading${index}`}>
                        <button
                          className="accordion-button"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#collapse${index}`}
                          aria-expanded={index === 0 ? "true" : "false"}
                          aria-controls={`collapse${index}`}
                        >
                          {subService.title}
                        </button>
                      </h3>
                      <div
                        id={`collapse${index}`}
                        className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
                        aria-labelledby={`heading${index}`}
                        data-bs-parent="#servicesAccordion"
                      >
                        <div className="accordion-body">
                          <p>{subService.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-5">
              <h2 className="fw-bold mb-4">Key Features</h2>
              <ul className="list-group list-group-flush">
                {solution.features.map((feature, index) => (
                  <li key={index} className="list-group-item d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-3"></i>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="fw-bold mb-4">Why Choose Us</h2>
              <Row className="g-4">
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <i className="bi bi-trophy-fill text-warning fs-4 me-3"></i>
                        <h5 className="fw-bold mb-0">Expertise</h5>
                      </div>
                      <Card.Text className="text-muted">
                        Our team of certified professionals brings years of industry experience.
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <i className="bi bi-graph-up-arrow text-success fs-4 me-3"></i>
                        <h5 className="fw-bold mb-0">Proven Results</h5>
                      </div>
                      <Card.Text className="text-muted">
                        We have a track record of delivering successful solutions to our clients.
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </Col>

          <Col lg={4}>
            <Card className="border-0 shadow">
              <Card.Body className="p-4">
                <h3 className="fw-bold mb-4">Get Started Today</h3>
                <p className="text-muted mb-4">
                  Contact us to learn more about our {solution.title} and how we can help your business.
                </p>
                <Button variant="danger" className="w-100 mb-3">
                  Request a Quote
                </Button>
                <Button variant="outline-primary" className="w-100">
                  Schedule a Consultation
                </Button>

                <hr className="my-4" />

                <h5 className="fw-bold mb-3">Contact Information</h5>
                <div className="d-flex align-items-center mb-3">
                  <i className="bi bi-telephone-fill text-primary me-3"></i>
                  <span>+91 86085 98089</span>
                </div>
                <div className="d-flex align-items-center">
                  <i className="bi bi-envelope-fill text-primary me-3"></i>
                  <span>sales@sunshiny.co.in</span>
                </div>
              </Card.Body>
            </Card>

            {/* Related Solutions */}
            <Card className="border-0 shadow mt-4">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">Related Solutions</h5>
              </Card.Header>
              <Card.Body className="p-0">
                <ul className="list-group list-group-flush">
                  {allSolutions
                    .filter((item) => item.id !== solutionId)
                    .slice(0, 4)
                    .map((item) => (
                      <li key={item.id} className="list-group-item">
                        <a href={`/solutions/${item.id}`} className="text-decoration-none">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-arrow-right-circle text-primary me-2"></i>
                            {item.title}
                          </div>
                        </a>
                      </li>
                    ))}
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default SolutionDetail

